import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    mdiAlertCircleOutline,
    mdiCheckCircleOutline,
    mdiMapMarkerOutline,
    mdiQrcode,
    mdiQrcodeRemove,
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'
import { type ResidentComposerPayload } from '../conversations/ResidentComposer'
import { ResidentThreadShell } from '../conversations/ResidentThreadShell'
import { enterConversation } from '../conversations/enter-conversation'
import {
    closedLabelOf,
    useLabelScan,
    useMemberLabelResolve,
    useSubmitLabelReport,
    type ClosedLabelView,
    type PublicLabelView,
} from '../../hooks/useLabels'

/**
 * PublicLabelPage is what a resident lands on after scanning the QR sticker on
 * a piece of equipment. Design constraints that shaped it:
 *
 *  - Mobile first, one column, large touch targets. It is read standing in a
 *    corridor or a plant room, on a phone, often in poor light.
 *  - No app chrome, no navigation, no organization context. The reader has no
 *    account and nowhere else to go.
 *  - The payload is an explicit allow-list from the server: equipment type,
 *    name, a two-level location hint, and the organization's display name.
 *    Nothing else — this sticker is glued in a hall whose door stands open half
 *    the day.
 *  - Every failure mode — unknown code, retired label, unassigned label,
 *    deleted organization — arrives here identically, by design.
 */
export function PublicLabelPage({ code }: { code: string }) {
    const { t } = useTranslation('labels')
    const { data: view, isLoading, error } = useLabelScan(code)

    // Only fires when a session token is present. An anonymous visitor must
    // never trigger it: the whole point of the two-endpoint split is that the
    // public page asks for nothing it is not entitled to.
    const { data: memberView } = useMemberLabelResolve(code)
    const closedLabel = closedLabelOf(error)

    if (isLoading) {
        return (
            <div className="mx-auto max-w-md p-6 text-muted-foreground" data-testid="label-scan-loading">
                {t('scan.loading')}
            </div>
        )
    }

    // A label whose intake address was closed is the ONE public failure the
    // server does distinguish (410, spec 2026-09-10-01): the sticker is glued
    // to a wall and cannot be recalled, so "unknown label" would be a lie the
    // reader can disprove by looking at the code in their hand. It is not an
    // error state — nothing went wrong, the code is simply retired — so it
    // renders as a calm notice, above the member block.
    if (closedLabel) {
        return (
            <div className="mx-auto max-w-md space-y-6 p-6" data-testid="label-scan-closed">
                <RetiredNotice closed={closedLabel} code={code} />
                {memberView && <MemberActions view={memberView} />}
            </div>
        )
    }

    // A label that is unknown, retired, or not yet attached to equipment all
    // arrive here identically — the server refuses to distinguish them.
    //
    // The member block still renders underneath: a field tech scanning a blank
    // sticker gets "not found" as an anonymous reader would, plus the "assign
    // this label" affordance their session entitles them to. That is the whole
    // "stick first, scan to assign" rollout.
    if (error || !view) {
        return (
            <div className="mx-auto max-w-md space-y-6 p-6" data-testid="label-scan-not-found">
                <div>
                    <AppIcon tone="muted" path={mdiAlertCircleOutline} size="xl" className="mb-3" />
                    <h1 className="text-xl font-semibold text-foreground">{t('scan.not_found_title')}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{t('scan.not_found_body')}</p>
                    <p className="mt-6 font-mono text-xs text-muted-foreground">{code}</p>
                </div>
                {memberView && <MemberActions view={memberView} />}
            </div>
        )
    }

    return <ScannedLabel view={view} code={code} memberView={memberView} />
}

/**
 * A live sticker: identify the equipment, then hand over to the thread.
 *
 * The step lives HERE rather than inside the report section because step two is
 * not a section of this page any more — it is the conversation window itself
 * (spec 2026-09-18-05), full height, header and composer included, the very
 * shell the resident lands in one send later.
 */
function ScannedLabel({
    view,
    code,
    memberView,
}: {
    view: PublicLabelView
    code: string
    memberView?: PublicLabelView
}) {
    const [step, setStep] = useState<ReportStep>('closed')
    const [contact, setContact] = useState<ReportContact>({ email: '', phone: '' })

    if (step === 'compose') {
        return <ReportComposer view={view} code={code} contact={contact} />
    }

    return (
        <div className="mx-auto max-w-md space-y-6 p-4 pb-16 md:p-6" data-testid="label-scan-page">
            <EquipmentCard view={view} />
            <Separator />
            <ReportSection step={step} setStep={setStep} contact={contact} setContact={setContact} />
            {memberView && <MemberActions view={memberView} />}
        </div>
    )
}

type ReportStep = 'closed' | 'contact' | 'compose'
interface ReportContact {
    email: string
    phone: string
}

/**
 * RetiredNotice is what a closed QR code says (spec 2026-09-10-01).
 *
 * Deliberately NOT styled as an error: no destructive colour, no alert icon.
 * Nothing failed — a manager took this code out of service, and the reader is
 * standing in a corridor holding a sticker that is simply no longer the way in.
 * The one useful thing the page can still do is tell them where to write, so
 * the organization's own intake address gets the emphasis when there is one.
 */
function RetiredNotice({ closed, code }: { closed: ClosedLabelView; code: string }) {
    const { t } = useTranslation('labels')

    return (
        <div>
            {closed.org_display_name && (
                <p className="text-xs uppercase tracking-wide text-muted-foreground" data-testid="label-closed-org">
                    {closed.org_display_name}
                </p>
            )}
            <AppIcon tone="muted" path={mdiQrcodeRemove} size="xl" className="mb-3 mt-3" />
            <h1 className="text-xl font-semibold text-foreground" data-testid="label-closed-title">
                {t('scan.closed_title')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="label-closed-body">
                {t('scan.closed_body')}
            </p>
            {closed.contact_address && (
                <p className="mt-4 text-sm text-foreground" data-testid="label-closed-contact">
                    {t('scan.closed_contact')}{' '}
                    <a
                        href={`mailto:${closed.contact_address}`}
                        className="font-medium underline underline-offset-4"
                        data-testid="label-closed-contact-address"
                    >
                        {closed.contact_address}
                    </a>
                </p>
            )}
            <p className="mt-6 font-mono text-xs text-muted-foreground" data-testid="label-closed-code">
                {closed.label_code || code}
            </p>
        </div>
    )
}

function EquipmentCard({ view }: { view: PublicLabelView }) {
    return (
        <div data-testid="label-scan-identity">
            {view.org_display_name && (
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{view.org_display_name}</p>
            )}
            <h1 className="mt-1 text-2xl font-semibold text-foreground" data-testid="label-scan-name">
                {view.asset_name || view.asset_type_label}
            </h1>
            {view.asset_type_label && view.asset_name && (
                <p className="mt-1 text-sm text-muted-foreground">{view.asset_type_label}</p>
            )}
            {view.location && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-foreground">
                    <AppIcon tone="muted" path={mdiMapMarkerOutline} size="sm" className="shrink-0" />
                    <span data-testid="label-scan-location">{view.location}</span>
                </p>
            )}
            <p className="mt-4 flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                <AppIcon path={mdiQrcode} size="xs" className="shrink-0" />
                <span data-testid="label-scan-code">{view.code}</span>
            </p>
        </div>
    )
}

/**
 * Step one of the QR door: a way back, then the conversation.
 *
 * It used to be a capture surface of its own — a recorder, five photos, no
 * typing box at all — which meant someone who did not want to talk into their
 * phone in a stairwell had nothing left to do here, and meant a second composer
 * to maintain.
 *
 * Two rules did NOT change:
 *
 *  - both contact fields stay optional, with an explicit way past them. A
 *    report that costs an identity is a report that does not get made, and the
 *    ledger mints a per-declaration anonymous identity rather than folding
 *    strangers together;
 *  - nothing is persisted before the first message. The composer is local until
 *    the send, so a scan made out of curiosity leaves no row in a queue a
 *    manager has to read.
 */
function ReportSection({
    step,
    setStep,
    contact,
    setContact,
}: {
    step: ReportStep
    setStep: (step: ReportStep) => void
    contact: ReportContact
    setContact: (contact: ReportContact) => void
}) {
    const { t } = useTranslation('labels')

    if (step === 'closed') {
        return (
            <Button className="w-full" size="lg" onClick={() => setStep('contact')} data-testid="label-report-open">
                {t('scan.report_cta')}
            </Button>
        )
    }

    return (
        <form
            className="space-y-4"
            data-testid="label-report-contact"
            onSubmit={event => {
                event.preventDefault()
                setStep('compose')
            }}
        >
            <h2 className="text-lg font-semibold text-foreground">{t('scan.contact_title')}</h2>
            <p className="text-sm text-muted-foreground" data-testid="label-report-contact-intro">
                {t('scan.contact_intro')}
            </p>

            <div className="space-y-1.5">
                <Label htmlFor="label-report-email">{t('scan.report_email_label')}</Label>
                <Input
                    id="label-report-email"
                    type="email"
                    value={contact.email}
                    onChange={event => setContact({ ...contact, email: event.target.value })}
                    placeholder={t('scan.report_email_placeholder')}
                    data-testid="label-report-email"
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="label-report-phone">{t('scan.report_phone_label')}</Label>
                <Input
                    id="label-report-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={contact.phone}
                    onChange={event => setContact({ ...contact, phone: event.target.value })}
                    placeholder={t('scan.report_phone_placeholder')}
                    data-testid="label-report-phone"
                />
                <p className="text-xs text-muted-foreground">{t('scan.report_contact_help')}</p>
            </div>

            <Button type="submit" size="lg" className="w-full" data-testid="label-report-continue">
                {t('scan.contact_continue')}
            </Button>
            {/* The anonymous path, said out loud rather than left to be
                guessed from two empty fields. */}
            <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                    setContact({ email: '', phone: '' })
                    setStep('compose')
                }}
                data-testid="label-report-skip-contact"
            >
                {t('scan.contact_skip')}
            </Button>
        </form>
    )
}

/**
 * Step two: the conversation window, before the conversation exists.
 *
 * The SAME shell `/c/` renders (spec 2026-09-18-05) — header, message area,
 * composer pinned at the bottom — with an empty message list, the equipment as
 * the title, and no status badge and no reference, because there is no thread
 * to name yet. On send the page becomes the real thread, and because it is the
 * same component that transition is only the list and the header filling in.
 */
function ReportComposer({
    view,
    code,
    contact,
}: {
    view: PublicLabelView
    code: string
    contact: ReportContact
}) {
    const { t } = useTranslation('labels')
    const submit = useSubmitLabelReport(code)

    const send = useCallback(async (payload: ResidentComposerPayload) => {
        const result = await submit.mutateAsync({
            description: payload.body,
            contact_email: contact.email.trim() || undefined,
            contact_phone: contact.phone.trim() || undefined,
            voice: payload.voice,
            photos: payload.files,
        })
        // The page becomes the thread. A deployment whose ledger could not mint
        // one falls through to the acknowledgement below rather than stranding
        // the reader on a composer that just emptied itself.
        enterConversation(result.conversation_url ?? '')
    }, [submit, contact])

    // Reached only when the send succeeded and no thread came back with it.
    if (submit.isSuccess && !submit.data?.conversation_url) {
        return (
            <div className="mx-auto max-w-md space-y-6 p-4 pb-16 md:p-6" data-testid="label-report-acknowledged">
                <AppIcon path={mdiCheckCircleOutline} size="xl" className="mb-3" />
                <h2 className="text-lg font-semibold text-foreground">{t('scan.thanks_title')}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t('scan.thanks_body')}</p>
            </div>
        )
    }

    return (
        <ResidentThreadShell
            organizationName={view.org_display_name ?? ''}
            title={view.asset_name || view.asset_type_label || ''}
            assetName={view.location}
            empty
            emptyIntro={t('scan.report_intro')}
            pageTestId="label-report-form"
            composer={{
                onSend: send,
                sending: submit.isPending,
                error: reportError(t, submit.error as { status?: number } | null, submit.isError),
                placeholder: t('scan.report_placeholder'),
                testIdPrefix: 'label-report',
                bodyTestId: 'label-report-description',
                voiceTestIdPrefix: 'label-report-voice',
            }}
        />
    )
}

/**
 * The scan page's error copy, mapped off the status exactly as it was before
 * this surface became a composer: a retired label says so plainly rather than
 * "try again in a moment", which would be a lie; 413 and 422 are both "leave a
 * photo out"; 429 is the brake.
 */
function reportError(
    t: (key: string) => string,
    error: { status?: number } | null,
    isError: boolean,
): string | null {
    if (!isError) return null
    switch (error?.status) {
        case 410:
            return t('scan.closed_body')
        case 429:
            return t('scan.report_rate_limited')
        case 413:
        case 422:
            return t('scan.report_too_heavy')
        default:
            return t('scan.report_error')
    }
}

/**
 * MemberActions only renders for a reader who turned out to be signed in and a
 * member of the owning organization — the server decides that, not this page.
 */
function MemberActions({ view }: { view: PublicLabelView }) {
    const { t } = useTranslation('labels')

    if (!view.org_code) return null

    // Equipment scanned off a sticker opens on its Photo tab: the same
    // recognise-then-act surface this public page is, with the app's own
    // affordances behind it.
    const target = view.asset_uid
        ? `/${view.org_code}/assets/${view.asset_uid}?tab=photo`
        : `/${view.org_code}/labels/${view.code}`

    return (
        <>
            <Separator />
            <a
                href={target}
                className="inline-flex text-sm font-medium text-foreground underline underline-offset-4"
                data-testid="label-scan-member-link"
            >
                {view.asset_uid ? t('scan.open_in_app') : t('scan.assign_cta')}
            </a>
        </>
    )
}
